import React, { useState } from "react";
import Modal from "./Modal";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
} from "lucide-react";

import { ViewMode } from '../App';

type ModalType = "app" | "phone" | "email" | "social" | null;

interface FooterProps {
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const Footer: React.FC<FooterProps> = ({ viewMode, setViewMode }) => {
  const { t } = useTranslation();
  const isEng = t('common.loading') === 'Loading...';
  const currentYear = 2024;
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case "app":
        return {
          title: isEng ? "Coming Soon" : "Em Breve",
          content: (
            <div className="space-y-4">
              <p className="text-gray-600">
                {isEng
                  ? "The MotoRush app will be available soon for iOS and Android. Stay tuned!"
                  : "O aplicação MotoRush estará disponível em breve para iOS e Android. Fique atento às novidades!"}
              </p>
              <div className="pt-4 border-t border-gray-100">
                <a
                  href="https://wa.me/244954009112"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 p-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all duration-300 font-bold shadow-md hover:shadow-green-200"
                >
                  <svg className="w-5 h-5 fill-current shrink-0" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.966C16.59 1.988 14.113.96 11.49.959c-5.442 0-9.866 4.372-9.87 9.802 0 1.63.45 3.224 1.302 4.633L1.87 20.887l5.51-1.442.267.159zm11.23-6.805c-.328-.164-1.94-.959-2.24-1.07-.3-.109-.52-.164-.74.164-.22.329-.85 1.07-1.04 1.29-.19.22-.38.24-.7.08-.328-.164-1.386-.51-2.64-1.627-.975-.87-1.633-1.946-1.824-2.274-.19-.329-.02-.507.143-.67.147-.146.328-.382.49-.574.164-.19.22-.328.328-.548.11-.22.055-.41-.03-.574-.08-.164-.74-1.78-.99-2.41-.247-.592-.5-.509-.685-.519-.178-.009-.383-.01-.588-.01-.205 0-.537.077-.82.383-.283.307-1.082 1.057-1.082 2.578 0 1.52 1.107 2.99 1.26 3.2 1.764 2.455 3.415 3.659 5.86 4.605 2.445.945 2.445.63 2.885.589.44-.041 1.94-.794 2.213-1.52.274-.727.274-1.348.192-1.472-.083-.124-.302-.197-.63-.362z" />
                  </svg>
                  <span>{isEng ? 'Chat on WhatsApp (954009112)' : 'Fale connosco pelo WhatsApp (954009112)'}</span>
                </a>
              </div>
            </div>
          ),
        };
      case "phone":
        return {
          title: isEng ? "Contacts" : "Contactos",
          content: (
            <div className="flex flex-col gap-4">
              <a
                href="tel:+244929214112"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="p-2 bg-orange-100 rounded-lg text-[#FF6B35]">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="font-bold text-gray-900">+244 929 214 112</div>
                  <div className="text-sm text-gray-500">{isEng ? "Calls" : "Chamadas"}</div>
                </div>
              </a>
              <a
                href="https://wa.me/244954009112"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="p-2 bg-green-100 rounded-lg text-green-600">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="font-bold text-gray-900">+244 954 009 112</div>
                  <div className="text-sm text-gray-500">{isEng ? "Calls and WhatsApp" : "Chamadas e WhatsApp"}</div>
                </div>
              </a>
            </div>
          ),
        };
      case "email":
        return {
          title: "Email",
          content: (
            <a
              href="mailto:motorushao@gmail.com"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                <Mail size={20} />
              </div>
              <div>
                <div className="font-bold text-gray-900">motorushao@gmail.com</div>
                <div className="text-sm text-gray-500">{isEng ? "Get in touch with us" : "Entre em contacto connosco"}</div>
              </div>
            </a>
          ),
        };
      case "social":
        return {
          title: isEng ? "Under Construction" : "Em Construção",
          content: (
            <p>
              {isEng
                ? "Our social media page is being created. Updates arriving soon!"
                : "A nossa página nesta rede social está a ser criada. Em breve teremos novidades!"}
            </p>
          ),
        };
      default:
        return { title: "", content: null };
    }
  };

  const modalData = getModalContent();

  const handleFooterLinkClick = (e: React.MouseEvent, href: string) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.substring(1);
      if (setViewMode) {
        if (targetId === 'about') {
          setViewMode('about');
        } else {
          // Se for uma secção B2B (sla ou b2b form), muda para a vista 'empresa'. Caso contrário, 'pessoal'.
          const targetMode: ViewMode = ['corporate-sla', 'b2b-lead-form'].includes(targetId) ? 'empresa' : 'pessoal';
          setViewMode(targetMode);
          setTimeout(() => {
            const element = document.getElementById(targetId);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 150);
        }
      } else {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  const isEmpresa = viewMode === 'empresa';

  const quickLinks = isEmpresa
    ? [
        { text: isEng ? "About Us" : "Quem Somos", href: "#about" },
        { text: isEng ? "Corporate SLA" : "SLA Corporativo", href: "#corporate-sla" },
        { text: isEng ? "Request Demo" : "Solicitar Demonstração", href: "#b2b-lead-form" },
        { text: isEng ? "B2B Support" : "Suporte B2B", href: "https://wa.me/244954009112" },
      ]
    : [
        { text: isEng ? "About Us" : "Quem Somos", href: "#about" },
        { text: t('header.howItWorks'), href: "#how-it-works" },
        { text: isEng ? "Features" : "Funcionalidades", href: "#download" },
        { text: isEng ? "Support" : "Suporte", href: "https://wa.me/244954009112" },
      ];

  const servicesTitle = isEmpresa
    ? (isEng ? "Corporate Solutions" : "Soluções B2B")
    : t('footer.services.title');

  const servicesList = isEmpresa
    ? [
        { text: isEng ? "Employee Commuting" : "Transporte de Colaboradores", href: "#corporate-sla" },
        { text: isEng ? "Freight Logistics" : "Logística de Mercadorias", href: "#corporate-sla" },
        { text: isEng ? "Unified ERP Billing" : "Facturação Única Mensal", href: "#corporate-sla" },
        { text: isEng ? "Fleet Real-time GPS" : "Telemetria e Gestão de Frota", href: "#corporate-sla" },
      ]
    : [
        { text: t('footer.services.passenger'), href: "#register" },
        { text: t('footer.services.delivery'), href: "#register" },
        { text: t('footer.services.scheduled'), href: "#register" },
        { text: t('footer.services.loyalty'), href: "#register" },
      ];

  const legalLinks = [
    { text: t('footer.legal.privacy'), mode: 'privacy' },
    { text: t('footer.legal.terms'), mode: 'terms' },
    { text: t('footer.legal.cookies'), mode: 'cookies' },
    { text: t('footer.legal.help'), mode: 'help' }
  ];

  return (
    <footer className="relative z-10 bg-gradient-to-br from-[#FF6B35] via-[#4A90E2] to-[#2C5282] text-white pt-16 pb-8">
      <Modal
        isOpen={activeModal !== null}
        onClose={() => setActiveModal(null)}
        title={modalData.title}
      >
        {modalData.content}
      </Modal>

      <div className="container mx-auto px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center font-bold text-lg">
                MR
              </div>
              <span className="text-2xl font-bold tracking-tight">
                MotoRush
              </span>
            </div>
            <p className="text-sm text-white/90 leading-relaxed mb-6">
              {t('footer.about')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: <Facebook size={20} />, href: "#", type: "social", label: "Facebook MotoRush" },
                { icon: <Instagram size={20} />, href: "https://www.instagram.com/motorush.ao/", type: "link", label: "Instagram Oficial MotoRush" },
                { icon: <Linkedin size={20} />, href: "#", type: "social", label: "LinkedIn MotoRush" },
                { icon: <Twitter size={20} />, href: "#", type: "social", label: "Twitter MotoRush" },
              ].map((social, index) => 
                social.type === "link" ? (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/25 transition-all transform hover:-translate-y-1 cursor-pointer text-white"
                  >
                    {social.icon}
                  </a>
                ) : (
                  <button
                    key={index}
                    type="button"
                    aria-label={social.label}
                    onClick={() => setActiveModal("social")}
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/25 transition-all transform hover:-translate-y-1 cursor-pointer text-white"
                  >
                    {social.icon}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3 text-sm">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/90 hover:text-white hover:underline underline-offset-4 transition-all"
                    onClick={(e) => {
                      if (link.href.startsWith("#")) {
                        handleFooterLinkClick(e, link.href);
                      }
                    }}
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block">
              {servicesTitle}
            </h4>
            <ul className="space-y-3 text-sm">
              {servicesList.map((service, index) => (
                <li key={index}>
                  <a
                    href={service.href}
                    onClick={(e) => handleFooterLinkClick(e, service.href)}
                    className="text-white/90 hover:text-white hover:underline underline-offset-4 transition-all flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-orange-300 rounded-full"></span>
                    {service.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 border-b border-white/10 pb-2 inline-block">
              {t('footer.contact')}
            </h4>
            <div className="flex flex-col gap-4 text-sm">
              {/* Phone */}
              <div
                onClick={() => setActiveModal("phone")}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="w-9 h-9 bg-white/10 group-hover:bg-white/20 transition-colors rounded-lg flex items-center justify-center shrink-0">
                  <Phone size={16} />
                </div>
                <span className="text-white/90 group-hover:text-white transition-colors">+244 929 214 112 / ...</span>
              </div>

              {/* Email */}
              <div
                onClick={() => setActiveModal("email")}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="w-9 h-9 bg-white/10 group-hover:bg-white/20 transition-colors rounded-lg flex items-center justify-center shrink-0">
                  <Mail size={16} />
                </div>
                <span className="text-white/90 group-hover:text-white transition-colors">motorushao@gmail.com</span>
              </div>

              {/* Address */}
              <a
                href="https://www.google.com/maps/search/?api=1&query=Talatona,+Luanda,+Angola"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 group text-white no-underline"
              >
                <div className="w-9 h-9 bg-white/10 group-hover:bg-white/20 transition-colors rounded-lg flex items-center justify-center mt-1 shrink-0">
                  <MapPin size={16} />
                </div>
                <span className="text-white/90 group-hover:text-white leading-relaxed transition-colors">
                  Talatona, Luanda<br />Angola
                </span>
              </a>

              {/* App Store original badges in footer */}
              {!isEmpresa && (
                <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-3">
                  <h5 className="text-xs font-bold uppercase tracking-wider opacity-70">{t('footer.appDownload')}</h5>
                  <button
                    onClick={() => setActiveModal("app")}
                    className="hover:opacity-80 transform hover:scale-105 transition duration-300 self-start"
                  >
                    <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" className="h-8 border border-white/20 rounded-md" />
                  </button>
                  <button
                    onClick={() => setActiveModal("app")}
                    className="hover:opacity-80 transform hover:scale-105 transition duration-300 self-start"
                  >
                    <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-8" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/20 pt-6">
          {/* Made in Angola */}
          <div className="text-center mb-4 text-sm opacity-80 font-medium flex items-center justify-center gap-2">
            <span>🇦🇴 {t('footer.madeIn')}</span>
          </div>

          {/* Copyright */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs opacity-80 order-2 md:order-1">
              <span>© {currentYear} MotoRush. {t('footer.rights')}</span>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-4 text-xs opacity-80 order-1 md:order-2">
              {legalLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => setViewMode && setViewMode(link.mode as ViewMode)}
                  className="hover:text-white transition-colors hover:underline"
                >
                  {link.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
